import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Download, Mail, Pencil } from 'lucide-react';
import api from '../lib/api';
import Header from '../components/Layout/Header';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import useAuthStore from '../store/useAuthStore';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canSendEmails = user?.role === 'admin' || user?.role === 'editor';
  
  const [project, setProject] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    transaction_type: 'Payment Received',
    amount: '',
    payment_mode_id: '',
    received_paid_by: '',
    description: '',
  });

  useEffect(() => {
    loadProjectData();
  }, [id]);

  const loadProjectData = async () => {
    try {
      const [projectRes, paymentsRes, modesRes, clientsRes, catsRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/payments`),
        api.get('/payment-modes'),
        api.get('/clients'),
        api.get('/categories?type=project'),
      ]);
      setProject(projectRes.data);
      setPayments(paymentsRes.data);
      setPaymentModes(modesRes.data);
      setClients(clientsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = () => {
    setEditForm({
      name: project.name,
      client_id: project.client_id,
      category_id: project.category_id,
      total_value: project.total_value,
      status: project.status,
      description: project.description || '',
      start_date: project.start_date ? project.start_date.toString().slice(0, 10) : '',
      expected_end_date: project.expected_end_date ? project.expected_end_date.toString().slice(0, 10) : '',
    });
    setEditDialogOpen(true);
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/projects/${id}`, {
        ...editForm,
        total_value: parseFloat(editForm.total_value),
        start_date: editForm.start_date || null,
        expected_end_date: editForm.expected_end_date || null,
      });
      toast.success('Project updated');
      setEditDialogOpen(false);
      loadProjectData();
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${id}/payments`, {
        ...formData,
        project_id: id,
        date: new Date(formData.date).toISOString(),
        amount: parseFloat(formData.amount),
      });
      toast.success('Payment added successfully');
      setDialogOpen(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        transaction_type: 'Payment Received',
        amount: '',
        payment_mode_id: '',
        received_paid_by: '',
        description: '',
      });
      loadProjectData();
    } catch (error) {
      toast.error('Failed to add payment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadInvoice = async () => {
    try {
      const response = await api.get(`/projects/${id}/invoice/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project.name}_invoice.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const sendPaymentReminder = async () => {
    if (!recipientEmail) {
      toast.error('Please enter recipient email');
      return;
    }
    try {
      await api.post(`/projects/${id}/send-reminder?recipient_email=${recipientEmail}`);
      toast.success('Payment reminder sent successfully');
      setEmailDialogOpen(false);
      setRecipientEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const sendInvoiceEmail = async () => {
    if (!recipientEmail) {
      toast.error('Please enter recipient email');
      return;
    }
    try {
      await api.post(`/projects/${id}/send-invoice?recipient_email=${recipientEmail}`);
      toast.success('Invoice sent successfully');
      setEmailDialogOpen(false);
      setRecipientEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Failed to load project details.</p>
          <Button onClick={() => navigate('/projects')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  const progressPercentage = (project.received_amount / project.total_value) * 100;

  return (
    <div className="min-h-screen">
      <Header title={project.name} subtitle="Project details and payment logs" />

      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <Button
            onClick={() => navigate('/projects')}
            variant="outline"
            className="border-slate-200 hover:bg-slate-50"
            data-testid="back-to-projects-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Back to Projects
          </Button>
          <Button
            onClick={openEditDialog}
            variant="outline"
            className="border-orange-200 text-orange-600 hover:bg-orange-50"
          >
            <Pencil className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Edit Project
          </Button>
        </div>

        {/* Edit Project Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditProject} className="space-y-4">
              <div className="space-y-2">
                <Label>Project Name</Label>
                <Input
                  value={editForm.name || ''}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  required
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={editForm.client_id || ''} onValueChange={v => setEditForm({ ...editForm, client_id: v })}>
                  <SelectTrigger className="bg-slate-50"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={editForm.category_id || ''} onValueChange={v => setEditForm({ ...editForm, category_id: v })}>
                  <SelectTrigger className="bg-slate-50"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Total Value (INR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.total_value || ''}
                  onChange={e => setEditForm({ ...editForm, total_value: e.target.value })}
                  required
                  className="bg-slate-50"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={editForm.start_date || ''}
                    onChange={e => setEditForm({ ...editForm, start_date: e.target.value })}
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expected End Date</Label>
                  <Input
                    type="date"
                    value={editForm.expected_end_date || ''}
                    onChange={e => setEditForm({ ...editForm, expected_end_date: e.target.value })}
                    className="bg-slate-50"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.status || ''} onValueChange={v => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="bg-slate-50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="On Hold">On Hold</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editForm.description || ''}
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Optional notes"
                  className="bg-slate-50"
                />
              </div>
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                Save Changes
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
            <p className="text-sm font-medium text-slate-500 mb-2">Total Project Value</p>
            <p className="text-3xl font-heading font-bold text-slate-900">{formatCurrency(project.total_value)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
            <p className="text-sm font-medium text-slate-500 mb-2">Total Received</p>
            <p className="text-3xl font-heading font-bold text-emerald-600">{formatCurrency(project.received_amount)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
            <p className="text-sm font-medium text-slate-500 mb-2">Due Balance</p>
            <p className="text-3xl font-heading font-bold text-red-600">{formatCurrency(project.due_balance)}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm mb-8">
          <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Payment Progress</h3>
          <Progress value={progressPercentage} className="h-3 mb-2" />
          <p className="text-sm text-slate-600 text-right">{progressPercentage.toFixed(1)}% completed</p>
          
          <div className="flex gap-3 mt-6">
            <Button
              onClick={downloadInvoice}
              variant="outline"
              className="border-slate-200"
              data-testid="download-invoice-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Invoice PDF
            </Button>
            
            {canSendEmails && (
              <>
                <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-orange-200 text-orange-600 hover:bg-orange-50"
                      data-testid="send-reminder-button"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Payment Reminder
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Send Email</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Recipient Email</Label>
                        <Input
                          type="email"
                          placeholder="client@example.com"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          className="bg-slate-50"
                          data-testid="recipient-email-input"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button
                          onClick={sendPaymentReminder}
                          className="flex-1 bg-orange-500 hover:bg-orange-600"
                          data-testid="send-reminder-confirm"
                        >
                          Send Payment Reminder
                        </Button>
                        <Button
                          onClick={sendInvoiceEmail}
                          variant="outline"
                          className="flex-1"
                          data-testid="send-invoice-confirm"
                        >
                          Send Invoice with PDF
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-50 flex items-center justify-between">
            <h3 className="text-lg font-heading font-semibold text-slate-900">Payment Logs</h3>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white active:scale-95" data-testid="add-payment-button">
                  <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Add Payment
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Payment Log</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddPayment} className="space-y-4" data-testid="add-payment-form">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount (INR)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      className="bg-slate-50"
                      data-testid="payment-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Mode</Label>
                    <Select value={formData.payment_mode_id} onValueChange={(value) => setFormData({ ...formData, payment_mode_id: value })} required>
                      <SelectTrigger className="bg-slate-50">
                        <SelectValue placeholder="Select payment mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentModes.map((mode) => (
                          <SelectItem key={mode.id} value={mode.id}>
                            {mode.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Received From</Label>
                    <Input
                      value={formData.received_paid_by}
                      onChange={(e) => setFormData({ ...formData, received_paid_by: e.target.value })}
                      required
                      placeholder="Client name"
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Payment notes"
                      className="bg-slate-50"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="submit-payment-button">
                    Add Payment
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="payment-logs-table">
              <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4 text-left">Date</th>
                  <th className="px-6 py-4 text-left">Type</th>
                  <th className="px-6 py-4 text-right">Amount</th>
                  <th className="px-6 py-4 text-left">Mode</th>
                  <th className="px-6 py-4 text-left">Received From</th>
                  <th className="px-6 py-4 text-left">Description</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                      No payment logs yet
                    </td>
                  </tr>
                ) : (
                  payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {format(new Date(payment.date), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{payment.transaction_type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-emerald-600 font-semibold">
                        +{formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {paymentModes.find((m) => m.id === payment.payment_mode_id)?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{payment.received_paid_by}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{payment.description || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;